import { TodoIntegrationType } from '@demind-inc/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthContext, useGenerateTodoAuthURL, useTodoIntegrations } from '../data-access';
import { Path } from '../routing';
import {
  buildLocalTodoOAuthCallbackRoute,
  buildTodoOAuthCallbackUrl,
  getTodoOAuthCallbackPath,
  isDesktopApp,
  trackEventMixpanel,
  useFeatureFlag,
} from '../utils';

interface UseStartTodoAuthParams {
  redirectTo?: string;
}

export const useStartTodoAuth = ({ redirectTo = Path.DASHBOARD }: UseStartTodoAuthParams) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { generateTodoAuthUrl } = useGenerateTodoAuthURL();
  const { todoIntegrations: connectedTodos, isLoading: isFetchingTodoIntegrations } =
    useTodoIntegrations({ userId: user.userId });
  const [generatingTodoAuth, setGeneratingTodoAuth] = useState<TodoIntegrationType | undefined>();
  const { featureFlags } = useFeatureFlag();

  const isTodoTypeEnabled = (todoType: TodoIntegrationType) => {
    if (todoType === 'github') {
      return !!featureFlags.FF_github_integrations;
    }
    if (todoType === 'notion') {
      return !!featureFlags.FF_notion_integ;
    }
    return true;
  };

  const handleOpenTodoAuth = async (todoType: TodoIntegrationType) => {
    if (!isTodoTypeEnabled(todoType)) {
      return;
    }

    const callback = buildTodoOAuthCallbackUrl(todoType, getTodoOAuthCallbackPath(todoType), {
      redirectTo,
      userId: user.userId,
      isMobileApp: false,
    });

    setGeneratingTodoAuth(todoType);

    try {
      const authUrl = await generateTodoAuthUrl({ todoType, callback });
      trackEventMixpanel('click_connect_todo_auth', { todoType });

      if (isDesktopApp() && window.lifestack?.startTodoDesktopAuth) {
        const { callbackUrl } = await window.lifestack.startTodoDesktopAuth({
          authUrl,
          callbackUrl: callback,
        });
        navigate(buildLocalTodoOAuthCallbackRoute(callbackUrl), { replace: true });
        return;
      }

      window.location.href = authUrl;
    } finally {
      setGeneratingTodoAuth(undefined);
    }
  };

  return { connectedTodos, generatingTodoAuth, isFetchingTodoIntegrations, handleOpenTodoAuth };
};
